/**
 * SDK/CLI Store
 * Zustand store for managing SDK keys and CLI sessions
 */

import { create } from 'zustand';
import type {
  SDKApiKey,
  CreateSDKKeyInput,
  SDKKeyUsageStats,
  CLISession,
} from './types';
import * as api from './api';

interface SDKState {
  // SDK Keys
  keys: SDKApiKey[];
  keysLoading: boolean;
  keysError: string | null;
  selectedKey: SDKApiKey | null;
  newKeyValue: string | null;

  // Usage
  usage: SDKKeyUsageStats | null;
  usageLoading: boolean;

  // CLI Session (for device auth page)
  cliSession: CLISession | null;
  cliSessionLoading: boolean;
  cliSessionError: string | null;

  // Actions - SDK Keys
  loadKeys: () => Promise<void>;
  loadKey: (id: string) => Promise<SDKApiKey>;
  createKey: (input: CreateSDKKeyInput) => Promise<{ key: SDKApiKey; fullKey: string }>;
  updateKey: (id: string, input: Partial<CreateSDKKeyInput>) => Promise<void>;
  revokeKey: (id: string) => Promise<void>;
  rotateKey: (id: string) => Promise<string>;
  loadUsage: (id: string, days?: number) => Promise<void>;
  setSelectedKey: (key: SDKApiKey | null) => void;
  clearNewKeyValue: () => void;

  // Actions - CLI
  loadCLISession: (userCode: string) => Promise<void>;
  authorizeCLI: (userCode: string) => Promise<void>;
  denyCLI: (userCode: string) => Promise<void>;
  clearCLISession: () => void;

  clearErrors: () => void;
}

export const useSDKStore = create<SDKState>((set, get) => ({
  // Initial state
  keys: [],
  keysLoading: false,
  keysError: null,
  selectedKey: null,
  newKeyValue: null,

  usage: null,
  usageLoading: false,

  cliSession: null,
  cliSessionLoading: false,
  cliSessionError: null,

  // SDK Key Actions
  loadKeys: async () => {
    set({ keysLoading: true, keysError: null });
    try {
      const response = await api.listSDKKeys();
      set({ keys: response.data, keysLoading: false });
    } catch (error) {
      set({
        keysError: (error as Error).message,
        keysLoading: false,
      });
    }
  },

  loadKey: async (id: string) => {
    try {
      const response = await api.getSDKKey(id);
      set({ selectedKey: response.data });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createKey: async (input: CreateSDKKeyInput) => {
    set({ keysLoading: true, keysError: null });
    try {
      const response = await api.createSDKKey(input);
      set((state) => ({
        keys: [...state.keys, response.data.key],
        newKeyValue: response.data.full_key,
        selectedKey: response.data.key,
        keysLoading: false,
      }));
      return {
        key: response.data.key,
        fullKey: response.data.full_key,
      };
    } catch (error) {
      set({
        keysError: (error as Error).message,
        keysLoading: false,
      });
      throw error;
    }
  },

  updateKey: async (id: string, input: Partial<CreateSDKKeyInput>) => {
    set({ keysLoading: true, keysError: null });
    try {
      const response = await api.updateSDKKey(id, input);
      set((state) => ({
        keys: state.keys.map((k) => (k.id === id ? response.data : k)),
        selectedKey: state.selectedKey?.id === id ? response.data : state.selectedKey,
        keysLoading: false,
      }));
    } catch (error) {
      set({
        keysError: (error as Error).message,
        keysLoading: false,
      });
      throw error;
    }
  },

  revokeKey: async (id: string) => {
    set({ keysLoading: true, keysError: null });
    try {
      await api.revokeSDKKey(id);
      set((state) => ({
        keys: state.keys.filter((k) => k.id !== id),
        selectedKey: state.selectedKey?.id === id ? null : state.selectedKey,
        keysLoading: false,
      }));
    } catch (error) {
      set({
        keysError: (error as Error).message,
        keysLoading: false,
      });
      throw error;
    }
  },

  rotateKey: async (id: string) => {
    set({ keysLoading: true, keysError: null });
    try {
      const response = await api.rotateSDKKey(id);
      set((state) => ({
        keys: state.keys.map((k) => (k.id === id ? response.data.key : k)),
        selectedKey: state.selectedKey?.id === id ? response.data.key : state.selectedKey,
        newKeyValue: response.data.full_key,
        keysLoading: false,
      }));
      return response.data.full_key;
    } catch (error) {
      set({
        keysError: (error as Error).message,
        keysLoading: false,
      });
      throw error;
    }
  },

  loadUsage: async (id: string, days: number = 30) => {
    set({ usageLoading: true });
    try {
      const response = await api.getSDKKeyUsage(id, days);
      set({ usage: response.data, usageLoading: false });
    } catch (error) {
      set({ usageLoading: false });
      throw error;
    }
  },

  setSelectedKey: (key: SDKApiKey | null) => {
    set({ selectedKey: key, usage: null });
  },

  clearNewKeyValue: () => {
    set({ newKeyValue: null });
  },

  // CLI Actions
  loadCLISession: async (userCode: string) => {
    set({ cliSessionLoading: true, cliSessionError: null });
    try {
      const response = await api.getCLISession(userCode);
      set({ cliSession: response.data, cliSessionLoading: false });
    } catch (error) {
      set({
        cliSessionError: (error as Error).message,
        cliSessionLoading: false,
      });
    }
  },

  authorizeCLI: async (userCode: string) => {
    set({ cliSessionLoading: true, cliSessionError: null });
    try {
      const response = await api.authorizeCLIDevice(userCode);
      set({ cliSession: response.data, cliSessionLoading: false });
    } catch (error) {
      set({
        cliSessionError: (error as Error).message,
        cliSessionLoading: false,
      });
      throw error;
    }
  },

  denyCLI: async (userCode: string) => {
    set({ cliSessionLoading: true, cliSessionError: null });
    try {
      const response = await api.denyCLIDevice(userCode);
      set({ cliSession: response.data, cliSessionLoading: false });
    } catch (error) {
      set({
        cliSessionError: (error as Error).message,
        cliSessionLoading: false,
      });
      throw error;
    }
  },

  clearCLISession: () => {
    set({ cliSession: null, cliSessionError: null });
  },

  clearErrors: () => {
    set({ keysError: null, cliSessionError: null });
  },
}));

// Selectors
export const useSDKKeys = () => useSDKStore((state) => state.keys);
export const useSelectedSDKKey = () => useSDKStore((state) => state.selectedKey);
export const useSDKKeyUsage = () => useSDKStore((state) => state.usage);
export const useNewKeyValue = () => useSDKStore((state) => state.newKeyValue);
export const useCLISession = () => useSDKStore((state) => state.cliSession);

// Active keys selector
export const useActiveSDKKeys = () =>
  useSDKStore((state) => state.keys.filter((k) => k.is_active));
